import React, { useId } from 'react';
import { cn } from '@/shared/lib/cn';

interface FieldErrorProps {
    id?: string;
    children?: React.ReactNode;
    className?: string;
}

/** Mensagem de erro anunciada por leitores de ecrã assim que aparece. */
function FieldError({ id, children, className }: FieldErrorProps) {
    if (!children) return null;
    return (
        <p
            id={id}
            role="alert"
            className={cn('mt-1.5 text-xs font-medium text-[var(--status-danger)]', className)}
        >
            {children}
        </p>
    );
}

interface FormFieldProps {
    label?: React.ReactNode;
    /** String com a mensagem, ou nada quando o campo está válido. */
    error?: string | null;
    hint?: React.ReactNode;
    required?: boolean;
    className?: string;
    htmlFor?: string;
    /**
     * Recebe os atributos a espalhar no input (`id`, `aria-invalid`, `aria-describedby`),
     * evitando que cada formulário volte a cablar isso à mão.
     */
    children: React.ReactNode | ((props: FieldControlProps) => React.ReactNode);
}

interface FieldControlProps {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby'?: string;
}

export function FormField({
    label,
    error,
    hint,
    required,
    className,
    htmlFor,
    children,
}: FormFieldProps) {
    const auto = useId();
    const id = htmlFor ?? auto;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;
    const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

    const control: FieldControlProps = {
        id,
        'aria-invalid': Boolean(error),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    };

    return (
        <div className={cn('w-full', className)}>
            {label && (
                <label htmlFor={id} className="ds-label">
                    {label}
                    {required && (
                        <span className="ml-0.5 text-[var(--status-danger)]" aria-hidden="true">
                            *
                        </span>
                    )}
                </label>
            )}
            {typeof children === 'function' ? children(control) : children}
            {hint && !error && (
                <p id={hintId} className="mt-1.5 text-xs text-slate-600 dark:text-zinc-400">
                    {hint}
                </p>
            )}
            <FieldError id={errorId}>{error}</FieldError>
        </div>
    );
}


